import { FC, useEffect, useMemo, useState } from "react";

import { Legend } from "../../../Charts/Legend/Legend";
import { LegendProps } from "../../../Charts/Legend/Legend.type";
import { LineChart } from "../../../Charts/LineChart/LineChart";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";

import { WEEE_FLOW_IN_THE_HOME_APPLIANCE_SECTOR_CHART_DATA_KEYS } from "./WeeeFlowInTheHomeApplianceSector.const";
import { WeeeFlowInTheHomeApplianceSectorLineChartToolTip } from "./tooltip/WeeeFlowInTheHomeApplianceSectorLineChartToolTip";
import { WeeeFlowInTheHomeApplianceSectorProps } from "./WeeeFlowInTheHomeApplianceSector.type";

const WeeeFlowInTheHomeApplianceSector: FC<WeeeFlowInTheHomeApplianceSectorProps> = ({ lineChartData }) => {
	const chartSize = useBreakpoint();

	const [legendDirection, setLegendDirection] = useState<LegendProps["direction"]>("horizontal");

	const [chartData, setChartData] = useState(lineChartData);

	const [marginLeft, setMarginLeft] = useState(-20);
	const [axisFontSize, setAxisFontSize] = useState(14);
	const [xAxisPadding, setXAxisPadding] = useState(50);
	const [chartHeight, setChartHeight] = useState(300);

	const legendData = useMemo(() => {
		return WEEE_FLOW_IN_THE_HOME_APPLIANCE_SECTOR_CHART_DATA_KEYS.map((dataKey) => {
			const label = typeof dataKey?.translation === "string" ? dataKey.translation : dataKey.name;

			return {
				color: dataKey.color,
				label: label,
			};
		});
	}, []);

	useEffect(() => {
		switch (chartSize) {
			case "xs":
				//setChartData(lineChartData.filter((dataEntry) => ["2010", "2015", "2020"].includes(dataEntry.name)));
				setMarginLeft(0);
				setAxisFontSize(11);
				setXAxisPadding(20);
				setChartHeight(300);
				setLegendDirection("vertical");
				break;

			case "sm":
				//setChartData(lineChartData.filter((dataEntry) => ["2008", "2010", "2015", "2017", "2020"].includes(dataEntry.name)));
				setMarginLeft(0);
				setAxisFontSize(11);
				setXAxisPadding(15);
				setChartHeight(360);
				setLegendDirection("vertical");
				break;

			case "md":
			case "lg":
			case "xl":
				//setChartData(lineChartData.filter((_, i) => i % 2 === 0));
				setMarginLeft(0);
				setAxisFontSize(14);
				setXAxisPadding(20);
				setChartHeight(360);
				setLegendDirection("horizontal");
				break;

			default:
				setChartData(lineChartData);
				setMarginLeft(0);
				setAxisFontSize(14);
				setXAxisPadding(35);
				setChartHeight(360);
				setLegendDirection("horizontal");
				break;
		}
	}, [chartSize, lineChartData]);

	// On the smaller breakpoints the plot is too cramped to hold the legend without covering the lines,
	// so it keeps its original place underneath the chart there.
	const overlayLegend = chartSize !== "xs" && chartSize !== "sm";

	return (
		<div className="flex flex-col gap-8 mb-4">

			<div className="relative">

				<LineChart
					data={chartData}
					height={chartHeight}
					marginLeft={marginLeft}
					XAxisProps={{
						dataKey: "name",
						padding: { left: xAxisPadding, right: xAxisPadding },
						tick: { fontSize: axisFontSize },
					}}
					xDomain={[2012,2021]}
					customXTicks={[2012,2014,2016,2018,2020]}
					YAxisProps={{
						dataKey: "units",
						tickCount: 11,
						tick: { fontSize: axisFontSize },
						domain: [0, 5],
						interval: 0,
					}}
					yTickFractionDigits={1}
					dataKeys={WEEE_FLOW_IN_THE_HOME_APPLIANCE_SECTOR_CHART_DATA_KEYS}
					Tooltip={WeeeFlowInTheHomeApplianceSectorLineChartToolTip}
					YAxisLabel="WEEE flows in the home appliance sector"
				/>

				{overlayLegend && (
					<div className="absolute bottom-10 right-4 bg-white pointer-events-none">
						<Legend items={legendData} direction="vertical" compact />
					</div>
				)}

			</div>

			{!overlayLegend && (
				<div className="self-stretch md:self-center">
					<Legend items={legendData} direction={legendDirection} />
				</div>
			)}

		</div>
	);
};

export { WeeeFlowInTheHomeApplianceSector };
